// apps/client/src/hooks/useLoanSearch.ts
// Debounced server-side loan search for the successor-loan picker.
//
// Adapted from the inline loan search in
// components/command-palette/useEntitySearch.ts (300ms debounce, monotonic
// request-id stale guard, >= 2 char gate). This version FIXES a bug in that
// hook: its early-return / disabled path left requestIdRef untouched, so a
// response from a request that was in flight when the box was cleared (or the
// search disabled) could still commit stale results. Here the request id is
// bumped on EVERY change of query / enabled / purpose and on unmount, so a
// response from any superseded request can never call setState with stale data.

import { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface LoanSearchResult {
  loanId: string;
  borrowerName: string;
  lender: string;
  loanAmountCents: number;
  settlementDate: string;
}

export interface UseLoanSearchOptions {
  enabled: boolean;
  purpose: 'all' | 'refinance_successor';
}

export interface UseLoanSearchState {
  loans: LoanSearchResult[];
  isLoading: boolean;
  error: string | null;
}

export function useLoanSearch(
  query: string,
  opts: UseLoanSearchOptions
): UseLoanSearchState {
  const { enabled, purpose } = opts;
  const [state, setState] = useState<UseLoanSearchState>({
    loans: [],
    isLoading: false,
    error: null,
  });

  // Monotonic request id. Every effect run (query / enabled / purpose change)
  // and every cleanup (rerun or unmount) bumps it, so an in-flight callable
  // captured with an older id can never commit its result.
  const requestIdRef = useRef(0);

  useEffect(() => {
    const currentRequestId = ++requestIdRef.current;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const trimmed = query.trim();
    if (!enabled || trimmed.length < 2) {
      // Idle: nothing loading, no error. The bump above already invalidated
      // any request that was in flight (e.g. the box was just cleared).
      setState({ loans: [], isLoading: false, error: null });
    } else {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      // Debounce (300ms).
      timeoutId = setTimeout(async () => {
        try {
          const searchLoansFn = httpsCallable<
            { query: string; purpose: string },
            LoanSearchResult[]
          >(functions, 'searchLoans');
          const result = await searchLoansFn({ query: trimmed, purpose });
          if (currentRequestId === requestIdRef.current) {
            setState({ loans: result.data ?? [], isLoading: false, error: null });
          }
        } catch (err) {
          if (currentRequestId === requestIdRef.current) {
            setState({
              loans: [],
              isLoading: false,
              error: err instanceof Error ? err.message : 'Loan search failed',
            });
          }
        }
      }, 300);
    }

    return () => {
      // Invalidate this run's in-flight response and cancel the pending
      // debounce. A superseded callable resolving later is silently dropped.
      requestIdRef.current += 1;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [query, enabled, purpose]);

  return state;
}
